/**
 * Which release a node runs, as the cloud shows it (D37 §4.1; the maintainer's "Release" column): in the portal's
 * server list (`SiteNodeWire.release`), the vendor's ops and rollout views, and `acp nodes list`.
 *
 * A release is named by its commit and when it was published — never by a raw hash on the page (the hashes are the
 * tooltip). The cloud knows it two ways: the node REPORTS it in its site-link `hello` (`release`, from this wave), or
 * the cloud INFERS it from the package hashes the node says it holds (the newest published release all of whose
 * packages it holds — which a downloaded, not yet swapped, release fools: only a report tells `updating` apart).
 *
 * `status`, against what the manifest serves the node (site pin → stage pin → latest, D15):
 *
 *   latest    it runs the latest release, and nothing pins it elsewhere;
 *   pinned    it runs what a pin serves it (a pin chose its release; the pin's note stays vendor-internal);
 *   updating  it holds every package of the release it is served but does not run it yet (a hot swap is due);
 *   behind    it runs something else and has not got the served release;
 *   waiting   (D40) it installed the served release, and the on-air guard holds the swap back while productions are
 *             active on it (`waiting` says which and how many);
 *   held      (D40) a backup its site's main has not proven the release for yet: it is served what it runs (`held`);
 *   rejected  (D40) it would not keep the release its site's rules name (a rollback or a refusal: `rejections`).
 *
 * Null when the cloud cannot tell (no release published, or a node that reported nothing the cloud recognises).
 * Open (D37 §4.3): a newer cloud may judge a state this reader does not know.
 */
import { z } from 'zod';
import { openEnum } from './openEnum.js';
import { releaseRejectionReasonWireSchema } from './nodeUpdate.js';
import { boundedIdWireSchema, isoTimestampWireSchema, recordedPackageHashWireSchema, sha256HexWireSchema } from './primitives.js';

/** A published release, as a person reads it: its commit and publication date; the list hash for the tooltip. */
export const releaseRefWireSchema = z
  .object({
    listHash: sha256HexWireSchema,
    commit: z.string().min(1).max(64),
    /** When its list was published (the release store's timestamp), or null when unknown. */
    publishedAt: isoTimestampWireSchema.nullable(),
  })
  .strict();
export type ReleaseRefWire = z.infer<typeof releaseRefWireSchema>;

export const NODE_RELEASE_STATUSES = ['latest', 'pinned', 'updating', 'behind', 'waiting', 'held', 'rejected'] as const;
export const nodeReleaseStatusWireSchema = openEnum(NODE_RELEASE_STATUSES);
export type NodeReleaseStatusWire = z.infer<typeof nodeReleaseStatusWireSchema>;

/** How the cloud knows what the node runs. Open: a newer cloud may learn it another way. */
export const nodeReleaseSourceWireSchema = openEnum(['reported', 'inferred']);

/**
 * Which rule chose what the node is served (the manifest's, D15). `production-pin`: a cloud production's task, which
 * runs the release its production was launched on (PLAN §9.2) — the column shows no status for it. Open, as the
 * rollout view's.
 */
const releaseTargetSourceWireSchema = openEnum(['site-pin', 'stage-pin', 'latest', 'production-pin', 'rollout']);

/** One live production on the node and the production package it is pinned to (§4.1: a production keeps its package for its whole show). */
export const productionReleaseWireSchema = z
  .object({
    productionId: boundedIdWireSchema,
    /** The production package hash the node reported for it, or null when it reported none. */
    packageHash: recordedPackageHashWireSchema.nullable(),
    /** The published release that package belongs to (the newest one naming it), or null when none does. */
    release: releaseRefWireSchema.nullable(),
  })
  .strict();
export type ProductionReleaseWire = z.infer<typeof productionReleaseWireSchema>;

/** One release a node would not keep (D40), as the cloud recorded it from the node's `release.rejected`. */
export const nodeReleaseRejectionWireSchema = z
  .object({
    /** The list, or null when the node could not name it. */
    listHash: sha256HexWireSchema.nullable(),
    /** The same, as a person reads it; null when the catalog does not know it. */
    release: releaseRefWireSchema.nullable(),
    reason: releaseRejectionReasonWireSchema,
    detail: z.string(),
    at: isoTimestampWireSchema,
    /** When the vendor cleared it (the node may try the release again), or null. */
    clearedAt: isoTimestampWireSchema.nullable(),
  })
  .strict();
export type NodeReleaseRejectionWire = z.infer<typeof nodeReleaseRejectionWireSchema>;

export const nodeReleaseWireSchema = z
  .object({
    /** What the node runs, or null when the cloud cannot tell. */
    running: releaseRefWireSchema.nullable(),
    source: nodeReleaseSourceWireSchema,
    status: nodeReleaseStatusWireSchema.nullable(),
    /** What the manifest serves it, and by which rule; `release` null before anything is published. */
    target: z.object({ source: releaseTargetSourceWireSchema, release: releaseRefWireSchema.nullable() }).strict(),
    /** The node's live productions (its last health report) and the package each runs. */
    productions: z.array(productionReleaseWireSchema),
    /** A release it installed and holds back (D40's on-air guard), as its last health report said; null: none. Absent from an older cloud. */
    waiting: z.object({ release: releaseRefWireSchema, productionsActive: z.number().int().nonnegative(), since: isoTimestampWireSchema }).strict().nullable().optional(),
    /**
     * A backup held on what it runs until its site's main proves the release its rules name (D40: main first) — that
     * release, and when the time limit serves it anyway (null: not before main proves it). Null: not held. Absent from
     * an older cloud.
     */
    held: z.object({ release: releaseRefWireSchema.nullable(), until: isoTimestampWireSchema.nullable() }).strict().nullable().optional(),
    /** The releases it would not keep (D40), newest first, with the vendor's clear. Absent from an older cloud. */
    rejections: z.array(nodeReleaseRejectionWireSchema).optional(),
  })
  .strict();
export type NodeReleaseWire = z.infer<typeof nodeReleaseWireSchema>;
